import { FailedEligibilityRuleDTO, IneligibleAssignmentDTO, IneligibleCreativeSelectionDTO } from 'models/creative'

import axios, { AxiosError } from 'axios'
import { CommonStatusCode } from 'constants/API'

/**
 * The admin creative-selection endpoint answers 422 for two unrelated reasons (ISSUE-2946):
 *
 * - the creative is **blacklisted** — a plain Spring error body carrying only a `message`, and
 * - the creative **failed eligibility rules** — the typed `IneligibleCreativeSelectionDTO` below.
 *
 * Only the second one is overridable, so the two must never be conflated. Both bodies carry a
 * `message`, so neither the status nor the message can discriminate them — the guard matches on the
 * payload *shape* instead, which is also why it stays tolerant of unknown `code` / `category` values
 * and of fields the backend may add later.
 */
function isFailedEligibilityRule(value: unknown): value is FailedEligibilityRuleDTO {
  if (!value || typeof value !== 'object') return false

  const rule = value as Partial<FailedEligibilityRuleDTO>

  // `code` and `category` stay open strings — the backend enums grow with every new rule — but they
  // must BE strings: the panel renders all three fields directly, so a null or object entry throws
  // during render, and the nearest boundary is the app-level one, which unmounts the whole SPA
  return typeof rule.code === 'string' && typeof rule.category === 'string' && typeof rule.message === 'string'
}

function isIneligibleAssignment(value: unknown): value is IneligibleAssignmentDTO {
  if (!value || typeof value !== 'object') return false

  const assignment = value as Partial<IneligibleAssignmentDTO>
  if (typeof assignment.assignmentId !== 'string') return false
  if (!Array.isArray(assignment.failedRules) || assignment.failedRules.length === 0) return false

  return assignment.failedRules.every(isFailedEligibilityRule)
}

/**
 * Narrows an unknown mutation error to the overridable eligibility rejection.
 *
 * An empty `ineligibleAssignments` returns false on purpose: with nothing to show, the admin would
 * be asked to justify a rejection we cannot explain, so the call falls through to the generic error
 * surface instead. The same reasoning applies one level down — an assignment with no rules, or with
 * a rule the panel could not render, is rejected rather than surfaced. Fails closed in every
 * ambiguous case.
 */
export function isIneligibleCreativeSelectionError(error: unknown): error is AxiosError<IneligibleCreativeSelectionDTO> {
  if (!axios.isAxiosError(error)) return false
  if (error.response?.status !== CommonStatusCode.UNPROCESSABLE_ENTITY) return false

  const data = error.response.data as Partial<IneligibleCreativeSelectionDTO> | undefined
  if (!data || typeof data !== 'object') return false
  if (!Array.isArray(data.ineligibleAssignments) || data.ineligibleAssignments.length === 0) return false

  return data.ineligibleAssignments.every(isIneligibleAssignment)
}
